import React from 'react';
import { nextRandomSeed, normalizeSeedBounds } from '../../../utils/seedRolling';
import Icon, { ICONS } from '../icons';
import Tooltip from '../Tooltip';

interface SeedConfig {
  min_value?: unknown;
  max_value?: unknown;
}

interface SeedInputProps {
  value: number | null;
  onChange: (value: number) => void;
  config: SeedConfig;
  checked?: boolean;
  onFixedChange?: (checked: boolean) => void;
  fixedLabel: string;
  fixedTooltip: string;
  className?: string;
}

export default function SeedInput({
  value,
  onChange,
  config,
  checked = false,
  onFixedChange,
  fixedLabel,
  fixedTooltip,
  className = '',
}: SeedInputProps) {
  const { min, max } = normalizeSeedBounds(config.min_value, config.max_value);

  // Handle manual input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty input while typing
    if (inputValue === '') {
      onChange(Math.max(min, Math.min(max, 0)));
      return;
    }
    
    // Parse and validate the number
    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed)) {
      // Clamp value to bounds
      const clamped = Math.max(min, Math.min(max, parsed));
      onChange(clamped);
    }
  };

  // Handle regenerate button click
  const handleRegenerate = () => {
    onChange(nextRandomSeed(min, max));
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <input
        type="number"
        value={value ?? ''}
        onChange={handleInputChange}
        min={min}
        max={max}
        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 hover:border-gray-500 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20 transition-all duration-200 text-sm"
        placeholder="Enter seed value..."
      />
      
      <Tooltip content="Generate random seed">
        <button
          type="button"
          onClick={handleRegenerate}
          aria-label="Generate random seed"
          className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-all duration-200 flex-shrink-0"
        >
          <Icon name={ICONS.REFRESH} size={16} />
        </button>
      </Tooltip>

      <Tooltip content={fixedTooltip}>
        <button
          type="button"
          onClick={() => onFixedChange?.(!checked)}
          aria-label={fixedLabel}
          aria-pressed={checked}
          className={`p-2 rounded-lg border transition-all duration-200 flex-shrink-0 ${
            checked
              ? 'bg-gradient-to-r from-blue-900/60 to-purple-900/60 border-blue-500/70 text-blue-100 hover:from-blue-800/60 hover:to-purple-800/60'
              : 'bg-gray-700 border-transparent hover:bg-gray-600 text-gray-300 hover:text-white'
          }`}
        >
          <Icon name={checked ? ICONS.LOCK : ICONS.UNLOCK} size={16} />
        </button>
      </Tooltip>
    </div>
  );
}
